import * as React from "react"
import * as SwitchPrimitives from "@radix-ui/react-switch"

import { cn } from "@/lib/utils"

/**
 * Switch — thin wrapper around @radix-ui/react-switch, mirroring this repo's
 * forwardRef + className-merge ui/* convention (see button.tsx, select.tsx).
 *
 * Hit target: the visible track is a standard 24x44px pill, but Tailwind's border-box
 * sizing shrinks that to a 20x40px padding box (h-6/w-11 minus the 2px border on each
 * side) before the `::before` inset is applied — an evenly-sized `-inset-[10px]` on that
 * padding box therefore only reaches 40x60px, 4px short of 44px on the shorter (vertical)
 * axis. `before:-inset-[12px]` reaches exactly 20+12+12=44px vertically (and 40+12+12=64px
 * horizontally, comfortably over 44px) — the same tap-target-expansion technique used for
 * small controls elsewhere, and the Radix-primitive equivalent of RepairComposer's 44px-tall
 * radio labels. Verified live via getBoundingClientRect/getComputedStyle(el, '::before')
 * (iteration-12 web-ui review, FM3).
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "relative peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors before:absolute before:-inset-[12px] before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      className
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className="pointer-events-none relative z-10 block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
